// What a lesson or course gate means for the signed-in officer. The Academy
// never hides gated content (reading about a feature is free); gates only
// label the card and disable "Start the walkthrough" when the officer could
// not follow it in their own account.
import { useAuth } from "../../context/AuthContext";
import { AcademyGate } from "../catalog/types";

export interface GateStatus {
  gate: AcademyGate;
  /** Short chip text. */
  label: string;
  /** True when the officer lacks what the gate needs. */
  locked: boolean;
  /** Why, in one sentence (tooltip). */
  reason: string;
}

export const GATE_LABELS: Record<AcademyGate, string> = {
  investigations: "Add-on",
  video: "Add-on",
  agency: "Agency",
  admin: "Admins",
  subscriber: "Subscribers",
};

export function useGateStatus(gate: AcademyGate | undefined): GateStatus | null {
  const { user, addons, subscriptionStatus } = useAuth();
  if (!gate) return null;
  const inAgency = Boolean(user?.agency_id);
  switch (gate) {
    case "investigations":
      return {
        gate,
        label: GATE_LABELS[gate],
        locked: !addons.investigations?.entitled,
        reason: "Needs an Investigations seat. Your department admin assigns seats from the agency dashboard.",
      };
    case "video":
      return {
        gate,
        label: GATE_LABELS[gate],
        locked: !addons.video?.entitled,
        reason: "Needs a Footage analysis seat. Your department admin assigns seats from the agency dashboard.",
      };
    case "agency":
      return { gate, label: GATE_LABELS[gate], locked: !inAgency, reason: "For officers whose department uses Police Narratives AI." };
    case "admin":
      return { gate, label: GATE_LABELS[gate], locked: !inAgency, reason: "For department administrators, in the agency dashboard." };
    case "subscriber":
      return {
        gate,
        label: GATE_LABELS[gate],
        locked: !inAgency && subscriptionStatus !== "active",
        reason: "Available once your subscription is active.",
      };
    default:
      return null;
  }
}
